import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus, Search, Pencil, Ban, RotateCcw, Truck, Receipt, X, Loader2,
    AlertTriangle, Info, PackageCheck, Undo2, CalendarDays,
} from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import AddSupplier from '../../components/modals/AddSupplier';
// `createAuthedAxios` is the DEFAULT EXPORT of hooks/useAxiosAuth.js — a plain
// factory that builds an axios instance, not a hook. Imported under its real
// name because eslint's react-hooks/rules-of-hooks keys on the identifier and
// would flag every call below as a misplaced hook. Same reasoning as
// hooks/usePermissions.js, which documents it at length.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';

const MySwal = withReactContent(Swal);

// WHO THE PHARMACY BUYS FROM, AND WHAT IT OWES THEM.
//
// Two screens in one, and the split is deliberate. The table is master data —
// the address book a buyer raises an order from, and the licence and GST numbers
// that make that order legal. The drawer is an ACCOUNT: every posted receipt and
// every credit note on one distributor, oldest first, with a running balance.
// They are on the same page because the question "should we keep buying from
// them" is never answered from one without the other.
//
// THE ONE THING THIS SCREEN MUST NEVER IMPLY
// The balance shown here is opening balance + posted purchases - settled
// credits. NOTHING HAS BEEN SUBTRACTED FOR MONEY ALREADY PAID — there is no
// supplier-payment document in the schema yet, so the cheque run is invisible to
// it. Read as "amount to pay today" it would cause the hospital to pay the same
// invoice twice, which is the single most expensive mistake a purchase
// department makes. The server says so on every response that carries a balance
// (`balanceExcludesPayments: true`) precisely so a client cannot render the
// figure as a payable without acknowledging the gap, and this screen honours
// that by printing the caveat beside the number rather than in a tooltip
// somebody has to go looking for.
//
// NOTHING HERE MOVES STOCK. Not one strip. A supplier is a party; stock comes
// into being in exactly one place, the goods receipt. There is no quantity on
// this screen and no call that could produce one.

// ---------------------------------------------------------------- money ----

// DECIMAL columns come back from the API as STRINGS. node-postgres returns
// NUMERIC as text on purpose, so the money does not drift through a float on the
// way here — which means `row.purchases + row.creditsSettled` CONCATENATES
// rather than adds. Every sum on this screen goes through paise and comes back
// out formatted, the same integer discipline the controller keeps on the other
// side of the wire.
const toPaise = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(n)) return 0;
    // Half away from zero rather than Math.round, which breaks ties toward
    // +Infinity and would round -2.5 to -2. A supplier balance is legitimately
    // negative (an advance paid, a credit larger than the purchases against it)
    // and must not be treated differently from its positive twin.
    const scaled = n * 100;
    return scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
};

const INR = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rupees = (value) => `₹${INR.format(toPaise(value) / 100)}`;

// ----------------------------------------------------------------- time ----

// THE LEDGER'S DAY IS INDIA'S, NOT THE BROWSER'S.
//
// The server filters this ledger with `(docDate AT TIME ZONE 'Asia/Kolkata')::date`
// — the receipt dates are timestamps, and cutting them in the hospital's own zone
// is what puts each document in the month the people arguing about it think it is
// in. If the browser then rendered the same timestamp in its own zone, a laptop
// set to London would show a receipt accepted at 02:00 IST on 1 July as dated
// 30 June, i.e. as a row that falls OUTSIDE the range that was just asked for.
// A statement whose rows contradict its own date filter is one nobody can
// reconcile against the distributor's copy. So the display zone is pinned to the
// same zone the filter uses.
const IST_DATE = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
});
const istDay = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '-' : IST_DATE.format(d);
};

// DATEONLY arrives as 'YYYY-MM-DD' and is NEVER passed through `new Date()`:
// that parses as UTC midnight, so a supplier invoice dated 2026-08-01 prints as
// 31 July for anybody west of Greenwich — a document that no longer matches the
// paper it was copied from.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dateOnly = (value) => {
    if (!value) return null;
    const [y, m, d] = String(value).slice(0, 10).split('-');
    if (!y || !m || !d) return String(value);
    return `${d} ${MONTHS[Number(m) - 1] || m} ${y}`;
};

// --------------------------------------------------------------- access ----

// The exact set Backend/api/routes/purchaseSupplierRoutes.js gates the ledger
// on. It is NARROWER than the read gate on the master itself, because the ledger
// is the hospital's trade position rather than an address book — a storekeeper
// needs a distributor's phone number to accept a carton and has no business
// reading what the hospital pays them. Asking first turns a 403 into a button
// that is simply not offered.
const LEDGER_PERMISSIONS = ['pharmacy.purchase.manage', 'pharmacy.return'];

const errorText = (error, fallback) =>
    error?.response?.data?.message || error?.message || fallback;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export default function Suppliers() {
    const { t } = useTranslation();
    const { can, canAny } = usePermissions();
    // One memoised instance: it reads the access token inside its request
    // interceptor, so a single copy stays correct across a token refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    // Adding or retiring a distributor changes who the hospital may buy from and
    // on what credit terms — a purchasing decision, so a purchasing permission.
    // Hiding the button is the polite half only; the route is the real gate.
    const canManage = can('pharmacy.purchase.manage');
    const canReadLedger = canAny(LEDGER_PERMISSIONS);

    const [suppliers, setSuppliers] = useState([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [activeTab, setActiveTab] = useState('active');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [mutating, setMutating] = useState(false);

    // The supplier whose account is open in the drawer, or null.
    const [ledgerFor, setLedgerFor] = useState(null);

    const searchTimeoutRef = useRef(null);

    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);
    const closeLedger = useCallback(() => setLedgerFor(null), []);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    // Escape closes the drawer. Bound on the page rather than inside the drawer
    // so it survives the drawer's own re-renders while its ledger page loads.
    useEffect(() => {
        if (!ledgerFor) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closeLedger(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [ledgerFor, closeLedger]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setListLoading(true);
            try {
                const res = await axiosInstance.get('/pharmacy/suppliers', {
                    params: {
                        page: currentPage,
                        limit: itemsPerPage,
                        search: debouncedSearch || undefined,
                        status: activeTab,
                    },
                });
                if (cancelled) return;
                setSuppliers(res?.data?.data || []);
                setTotal(res?.data?.total || 0);
            } catch (err) {
                if (cancelled) return;
                setSuppliers([]);
                setTotal(0);
                toast.warn(errorText(err, t('supplierpage.list_error', 'Failed to load the supplier list')));
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, debouncedSearch, activeTab, refreshKey]);

    const openCreate = () => { setEditing(null); setIsModalOpen(true); };
    const openEdit = (supplier) => { setEditing(supplier); setIsModalOpen(true); };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    /**
     * Retire a distributor. DELETE, and it deactivates rather than deleting:
     * purchase orders, receipts, returns and years of history all quote this row,
     * and a drug inspector asks the pharmacy to name the licensed dealer any
     * stock on its shelf came from.
     *
     * THE SERVER'S REFUSAL IS SHOWN VERBATIM AND THAT IS THE POINT. It refuses
     * while a 'placed' or 'partial' order is outstanding, because retiring the
     * party takes them out of every picker — so the van that is already loaded
     * arrives at the bay against an order whose supplier no longer appears
     * anywhere, and the receiving clerk's only way through is to create a SECOND
     * supplier row with the same name. That is exactly the split payables ledger
     * this master exists to prevent, so the message naming the open orders is
     * more useful than any wording invented here.
     */
    const handleRetire = async (supplier) => {
        const result = await MySwal.fire({
            title: t('supplierpage.retire_title', 'Retire this supplier?'),
            text: t('supplierpage.retire_text', 'They disappear from the order and receipt pickers. Nothing is deleted — every purchase already filed against them still names them, and their ledger stays readable so any credit still owed can be chased.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('supplierpage.retire', 'Retire'),
            cancelButtonText: t('supplierpage.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        setMutating(true);
        try {
            const res = await axiosInstance.delete(`/pharmacy/suppliers/${supplier.supplierId}`);
            // The server reports outstanding credit claims in its success
            // message — a distributor is often retired BECAUSE of the disputes,
            // and the money still owed does not stop the retirement but must not
            // vanish with it.
            toast.success(res?.data?.message || t('supplierpage.retired', 'Supplier retired'));
            refetch();
        } catch (err) {
            toast.warn(errorText(err, t('supplierpage.retire_error', 'Failed to retire the supplier')), { autoClose: 8000 });
        } finally {
            setMutating(false);
        }
    };

    // Bringing a party back can strand nothing, so unlike retiring it there is no
    // check to route around and a plain PUT is correct. The server accepts
    // status:'active' here and refuses status:'inactive' on the same endpoint —
    // see purchaseSupplierController.updateSupplier.
    const handleReactivate = async (supplier) => {
        setMutating(true);
        try {
            await axiosInstance.put(`/pharmacy/suppliers/${supplier.supplierId}`, { status: 'active' });
            toast.success(t('supplierpage.reactivated', 'Supplier is back on the list'));
            refetch();
        } catch (err) {
            toast.warn(errorText(err, t('supplierpage.reactivate_error', 'Failed to bring the supplier back')));
        } finally {
            setMutating(false);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    // --- row-level renderers -------------------------------------------------

    const GstinCell = ({ supplier }) => {
        if (!supplier.gstin) {
            // Not a cosmetic gap. Input tax credit is matched against the
            // supplier's GSTIN in GSTR-2B, so purchases from a party with none on
            // file are purchases whose tax the hospital cannot reclaim.
            return (
                <span
                    data-testid={`supplier-no-gstin-${supplier.supplierId}`}
                    title={t('supplierpage.no_gstin_hint', 'No GST number on file — input tax credit on their invoices cannot be matched')}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 text-amber-700 border border-dashed border-amber-300 text-[11px] font-semibold"
                >
                    <AlertTriangle className="w-3 h-3" />
                    {t('supplierpage.no_gstin', 'No GSTIN')}
                </span>
            );
        }
        return (
            <div>
                <div className="font-mono text-[11px] text-gray-700">{supplier.gstin}</div>
                {supplier.stateCode && (
                    <div className="text-[10px] text-slate-400">
                        {t('supplierpage.state', 'State {{code}}', { code: supplier.stateCode })}
                    </div>
                )}
            </div>
        );
    };

    const MoneyCell = ({ supplier }) => {
        const balanceP = toPaise(supplier.balance);
        const claimsP = toPaise(supplier.claimsOutstanding);
        return (
            <div>
                <div className={`text-sm font-bold ${balanceP > 0 ? 'text-gray-700' : balanceP < 0 ? 'text-emerald-700' : 'text-slate-400'}`}>
                    {rupees(supplier.balance)}
                </div>
                <div className="text-[10px] text-slate-400">
                    {t('supplierpage.purchases_n', '{{n}} receipts · {{value}}', {
                        n: Number(supplier.receiptCount) || 0,
                        value: rupees(supplier.purchases),
                    })}
                </div>
                {/* Money the hospital has claimed and NOT received. Deliberately
                    outside the balance: netting an unclaimed credit off the
                    payable makes accounts short-pay the supplier on the strength
                    of a credit note that may never arrive, and their next
                    statement then disagrees with ours by exactly the amount they
                    rejected. */}
                {claimsP > 0 && (
                    <span
                        data-testid={`supplier-claims-${supplier.supplierId}`}
                        className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold"
                    >
                        <Undo2 className="w-3 h-3" />
                        {t('supplierpage.claims_open', '{{value}} claimed, not credited', { value: rupees(supplier.claimsOutstanding) })}
                    </span>
                )}
            </div>
        );
    };

    const Actions = ({ supplier, compact = false }) => (
        <div className={`flex ${compact ? '' : 'justify-center'} gap-2`}>
            <button
                data-testid={`ledger-supplier-${supplier.supplierId}`}
                onClick={() => setLedgerFor(supplier)}
                disabled={!canReadLedger}
                title={canReadLedger
                    ? t('supplierpage.open_ledger', 'Open the account')
                    : t('supplierpage.no_ledger_permission', 'Reading a supplier account needs a purchase or return permission')}
                className={`text-[#2D8A8A] p-2 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 shadow-md transition-all duration-300 ${canReadLedger ? 'hover:text-white hover:from-[#4ABDBD] hover:to-[#2D8A8A] cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
            >
                <Receipt className="h-4 w-4" />
            </button>
            <button
                data-testid={`edit-supplier-${supplier.supplierId}`}
                onClick={() => openEdit(supplier)}
                disabled={!canManage}
                title={canManage
                    ? t('supplierpage.edit', 'Edit')
                    : t('supplierpage.no_permission', 'You do not have permission to change the supplier list')}
                className={`text-[#4ABDBD] p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md transition-all duration-300 ${canManage ? 'hover:text-white hover:from-[#4ABDBD] hover:to-[#3AA8A8] cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
            >
                <Pencil className="h-4 w-4" />
            </button>
            {supplier.status === 'active' ? (
                <button
                    data-testid={`retire-supplier-${supplier.supplierId}`}
                    onClick={() => handleRetire(supplier)}
                    disabled={!canManage || mutating}
                    title={t('supplierpage.retire', 'Retire')}
                    className={`text-red-500 p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-red-500 hover:to-pink-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <Ban className="h-4 w-4" />
                </button>
            ) : (
                <button
                    data-testid={`reactivate-supplier-${supplier.supplierId}`}
                    onClick={() => handleReactivate(supplier)}
                    disabled={!canManage || mutating}
                    title={t('supplierpage.reactivate', 'Bring back')}
                    className={`text-emerald-600 p-2 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 shadow-md transition-all duration-300 ${canManage && !mutating ? 'hover:text-white hover:from-emerald-500 hover:to-teal-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                >
                    <RotateCcw className="h-4 w-4" />
                </button>
            )}
        </div>
    );

    return (
        <div data-testid="suppliers-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="suppliers-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('supplierpage.title', 'Suppliers')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('supplierpage.subtitle', 'The distributors this pharmacy buys from — their licence, their GST number, the credit they allow, and what the hospital owes them.')}
                    </p>
                </div>
                <button
                    data-testid="add-supplier-button"
                    onClick={openCreate}
                    disabled={listLoading || !canManage}
                    title={canManage ? undefined : t('supplierpage.no_permission', 'You do not have permission to change the supplier list')}
                    className={`group flex items-center gap-2 w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${listLoading || !canManage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-xl hover:scale-105'}`}
                >
                    <Plus className="w-5 h-5" />
                    {t('supplierpage.add_supplier', 'Add supplier')}
                </button>
            </div>

            <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {['active', 'inactive'].map((tab) => (
                        <button
                            key={tab}
                            data-testid={`suppliers-tab-${tab}`}
                            onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab && (
                                <motion.div
                                    layoutId="activeSupplierStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">
                                {tab === 'active'
                                    ? t('supplierpage.tab_active', 'Buying from')
                                    : t('supplierpage.tab_inactive', 'Retired')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-96">
                    <input
                        data-testid="suppliers-search-input"
                        type="text"
                        placeholder={t('supplierpage.search_placeholder', 'Search name / contact / phone / GSTIN…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                        maxLength={60}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>
            </div>

            {/* THE CAVEAT THAT HAS TO BE ON THE FACE OF THE SCREEN. Every balance
                below is gross of payments already made, because nothing in this
                system records the cheque run yet. Printed here, not tucked into a
                tooltip, because the failure it prevents — paying an invoice twice
                — happens to somebody who never hovered. */}
            {canReadLedger && (
                <div data-testid="suppliers-balance-caveat" className="mb-6 flex items-start gap-2 rounded-xl border border-[#4ABDBD]/40 bg-[#4ABDBD]/5 px-3 py-2 text-[11px] text-[#2D8A8A]">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {t('supplierpage.balance_caveat', 'Balances are opening balance + posted receipts − settled credit notes. Payments the hospital has already made are NOT deducted — nothing here records the cheque run yet, so do not read a balance as the amount to pay today.')}
                </div>
            )}

            {listLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('supplierpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="suppliers-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('supplierpage.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('supplierpage.supplier', 'Supplier')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('supplierpage.contact', 'Contact')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('supplierpage.gstin', 'GSTIN')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('supplierpage.drug_licence', 'Drug licence')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('supplierpage.terms', 'Terms')}</th>
                                    {canReadLedger && (
                                        <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('supplierpage.balance', 'Balance')}</th>
                                    )}
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('supplierpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="suppliers-table-body">
                                {suppliers.length > 0 ? suppliers.map((s, index) => (
                                    <tr
                                        key={s.supplierId}
                                        data-testid={`supplier-row-${s.supplierId}`}
                                        className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300"
                                    >
                                        <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                        <td className="p-3 text-xs">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-lg bg-[#4ABDBD]/10 flex items-center justify-center flex-shrink-0">
                                                    <Truck className="w-4 h-4 text-[#2D8A8A]" />
                                                </div>
                                                <div>
                                                    <div className="font-semibold text-gray-700">{s.name || '-'}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3 text-xs text-gray-600">
                                            <div>{s.contactPerson || '—'}</div>
                                            <div className="text-[11px] text-slate-400">{s.phone || s.email || ''}</div>
                                        </td>
                                        <td className="p-3 text-xs"><GstinCell supplier={s} /></td>
                                        <td className="p-3 text-xs">
                                            {s.drugLicenseNo ? (
                                                <span className="font-mono text-[11px] text-gray-700">{s.drugLicenseNo}</span>
                                            ) : (
                                                // A wholesale licence number is what
                                                // the pharmacy shows when asked which
                                                // licensed dealer its stock came from.
                                                <span
                                                    title={t('supplierpage.no_licence_hint', 'No wholesale licence recorded for this dealer')}
                                                    className="inline-flex items-center gap-1 text-[11px] text-amber-700 font-semibold"
                                                >
                                                    <AlertTriangle className="w-3 h-3" />
                                                    {t('supplierpage.no_licence', 'Not recorded')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-3 text-xs">
                                            <span className="inline-flex px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-semibold">
                                                {Number(s.paymentTermDays) === 0
                                                    ? t('supplierpage.terms_cod', 'Cash')
                                                    : t('supplierpage.terms_net', 'Net {{days}}', { days: Number(s.paymentTermDays) || 0 })}
                                            </span>
                                            {Number(s.openOrders) > 0 && (
                                                <div className="text-[10px] text-slate-400 mt-1">
                                                    {t('supplierpage.open_orders', '{{n}} order(s) open', { n: Number(s.openOrders) })}
                                                </div>
                                            )}
                                        </td>
                                        {canReadLedger && <td className="p-3 text-xs"><MoneyCell supplier={s} /></td>}
                                        <td className="p-3 text-xs"><Actions supplier={s} /></td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan={canReadLedger ? 8 : 7} className="p-6 text-center text-gray-500">
                                            {activeTab === 'active'
                                                ? t('supplierpage.no_suppliers', 'No distributors on the list yet. Add one before raising a purchase order.')
                                                : t('supplierpage.no_retired', 'Nobody has been retired.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards below lg — eight columns do not survive a narrow
                        screen, and the purchase office is one of the places a
                        tablet is used. */}
                    <div className="block lg:hidden space-y-4">
                        {suppliers.length > 0 ? suppliers.map((s) => (
                            <div key={s.supplierId} data-testid={`supplier-card-${s.supplierId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                <div className="flex justify-between items-start gap-2 mb-2">
                                    <div>
                                        <div className="font-semibold text-gray-800 text-sm">{s.name || '-'}</div>
                                        <div className="text-xs text-gray-600 mt-1">
                                            {[s.contactPerson, s.phone].filter(Boolean).join(' · ') || '—'}
                                        </div>
                                    </div>
                                    <Actions supplier={s} compact />
                                </div>
                                <div className="flex flex-wrap items-center gap-2 mt-3">
                                    <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] text-gray-600">
                                        {Number(s.paymentTermDays) === 0
                                            ? t('supplierpage.terms_cod', 'Cash')
                                            : t('supplierpage.terms_net', 'Net {{days}}', { days: Number(s.paymentTermDays) || 0 })}
                                    </span>
                                    {s.gstin && (
                                        <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] font-mono text-gray-600">
                                            {s.gstin}
                                        </span>
                                    )}
                                    {s.drugLicenseNo && (
                                        <span className="px-2 py-0.5 rounded bg-white border border-gray-200 text-[11px] font-mono text-gray-600">
                                            {s.drugLicenseNo}
                                        </span>
                                    )}
                                </div>
                                {canReadLedger && <div className="mt-3"><MoneyCell supplier={s} /></div>}
                            </div>
                        )) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {activeTab === 'active'
                                    ? t('supplierpage.no_suppliers', 'No distributors on the list yet. Add one before raising a purchase order.')
                                    : t('supplierpage.no_retired', 'Nobody has been retired.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!listLoading && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <AddSupplier
                isOpen={isModalOpen}
                editing={editing}
                onClose={() => { setIsModalOpen(false); setEditing(null); }}
                onSaved={refetch}
            />

            <SupplierLedger
                supplier={ledgerFor}
                axiosInstance={axiosInstance}
                onClose={closeLedger}
            />
        </div>
    );
}

/* ==================================================================== *
 * THE SUPPLIER ACCOUNT — every posted receipt and every credit note on
 * one distributor, oldest first, with a running balance.
 * ==================================================================== */

// The shell exists separately from the body so AnimatePresence has a mounted
// parent to animate the exit from. Returning null from one component that owns
// both would unmount the whole subtree the moment the supplier clears, and the
// drawer would vanish instead of sliding away.
//
// The body is keyed on supplierId so switching straight from one distributor to
// another remounts it: its page number, date range and loaded rows are all about
// ONE account, and carrying a page-3 offset over to a supplier with four
// movements shows an empty ledger for a party that has one.
function SupplierLedger({ supplier, ...rest }) {
    return (
        <AnimatePresence>
            {supplier && <SupplierLedgerBody key={supplier.supplierId} supplier={supplier} {...rest} />}
        </AnimatePresence>
    );
}

function SupplierLedgerBody({ supplier, axiosInstance, onClose }) {
    const { t } = useTranslation();

    const [ledger, setLedger] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    // NO DEFAULT DATE RANGE. A supplier account is a running balance, and the
    // question it answers first is "what is the position" — which is the whole
    // history. A default window would make the brought-forward figure do work it
    // should only do when somebody deliberately asks for a period.
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const LIMIT = 25;

    // The server refuses an inverted range with a 400. Catching it here means the
    // user sees which field is wrong while still looking at it, rather than a
    // toast that only says the request failed.
    const rangeInvalid = Boolean(from && to && to < from);

    useEffect(() => {
        if (rangeInvalid) return undefined;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axiosInstance.get(`/pharmacy/suppliers/${supplier.supplierId}/ledger`, {
                    params: {
                        page,
                        limit: LIMIT,
                        // Only sent when complete. A half-typed '2026-08' reaches
                        // Postgres as a literal and comes back as a cast error,
                        // i.e. a 500 where the user needed nothing to happen yet.
                        from: ISO_DATE_RE.test(from) ? from : undefined,
                        to: ISO_DATE_RE.test(to) ? to : undefined,
                    },
                });
                if (!cancelled) setLedger(res?.data || null);
            } catch (err) {
                if (cancelled) return;
                setLedger(null);
                toast.warn(errorText(err, t('supplierpage.ledger_error', "Failed to load the supplier's account")));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplier.supplierId, page, from, to, rangeInvalid]);

    const rows = ledger?.data || [];
    const summary = ledger?.summary || null;
    const totalPages = ledger?.totalPages || 1;

    const setRange = (setter) => (value) => { setter(value); setPage(1); };

    return (
        <motion.div
            className="fixed inset-0 z-[9999] flex justify-end bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="supplier-ledger-backdrop"
        >
            <motion.aside
                role="dialog"
                aria-modal="true"
                aria-label={`Supplier account ${supplier.name}`}
                data-testid="supplier-ledger-drawer"
                className="h-full w-full max-w-3xl bg-white shadow-2xl overflow-y-auto"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', bounce: 0.1, duration: 0.45 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 z-10 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white p-4 flex items-start justify-between">
                    <div>
                        <div className="text-lg font-bold" data-testid="ledger-supplier-name">{supplier.name}</div>
                        <div className="text-[11px] opacity-90">
                            {[
                                supplier.supplierId,
                                supplier.gstin || t('supplierpage.no_gstin', 'No GSTIN'),
                                Number(supplier.paymentTermDays) === 0
                                    ? t('supplierpage.terms_cod', 'Cash')
                                    : t('supplierpage.terms_net', 'Net {{days}}', { days: Number(supplier.paymentTermDays) || 0 }),
                                supplier.status === 'inactive' ? t('supplierpage.retired_label', 'retired') : null,
                            ].filter(Boolean).join(' · ')}
                        </div>
                    </div>
                    <button
                        data-testid="close-supplier-ledger"
                        onClick={onClose}
                        className="p-2 rounded-xl hover:bg-white/20 cursor-pointer"
                        aria-label={t('supplierpage.close', 'Close')}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* ------------------------------------- period ---- */}
                    <div className="flex flex-col sm:flex-row sm:items-end gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <CalendarDays className="w-4 h-4 text-[#4ABDBD]" />
                            {t('supplierpage.period', 'Period')}
                        </div>
                        <input
                            data-testid="ledger-from"
                            type="date"
                            value={from}
                            onChange={(e) => setRange(setFrom)(e.target.value)}
                            className={`p-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${rangeInvalid ? 'border-red-300' : 'border-gray-300'}`}
                        />
                        <span className="text-xs text-slate-400">{t('supplierpage.to', 'to')}</span>
                        <input
                            data-testid="ledger-to"
                            type="date"
                            value={to}
                            onChange={(e) => setRange(setTo)(e.target.value)}
                            className={`p-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${rangeInvalid ? 'border-red-300' : 'border-gray-300'}`}
                        />
                        {(from || to) && (
                            <button
                                data-testid="ledger-clear-period"
                                onClick={() => { setFrom(''); setTo(''); setPage(1); }}
                                className="px-3 py-1.5 rounded-lg border border-gray-300 text-xs text-gray-600 hover:bg-gray-50 cursor-pointer"
                            >
                                {t('supplierpage.whole_account', 'Whole account')}
                            </button>
                        )}
                        <span className="text-[11px] text-slate-400 sm:ml-auto">
                            {t('supplierpage.ist_note', 'Dates are the Indian calendar day, matching the receipt.')}
                        </span>
                    </div>

                    {rangeInvalid && (
                        <p data-testid="ledger-range-error" className="text-[11px] text-red-600">
                            {t('supplierpage.range_invalid', 'The end of the period is before its start.')}
                        </p>
                    )}

                    {/* ------------------------------------ summary ---- */}
                    {summary && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <Stat
                                label={t('supplierpage.brought_forward', 'Brought forward')}
                                value={rupees(ledger.broughtForward)}
                                // Not decoration. A ledger opened for one quarter
                                // that started at zero would show a distributor who
                                // is owed six lakh as owed nothing — the classic way
                                // a statement gets signed off against the wrong
                                // number. It carries the opening balance plus every
                                // movement before the period.
                                hint={t('supplierpage.bf_hint', 'Opening balance plus everything before this period')}
                            />
                            <Stat
                                label={t('supplierpage.purchases', 'Receipts')}
                                value={rupees(summary.purchases)}
                                hint={t('supplierpage.purchases_hint', '{{n}} posted', { n: summary.receipts })}
                                tone="text-gray-700"
                            />
                            <Stat
                                label={t('supplierpage.credits_settled', 'Credits settled')}
                                value={`− ${rupees(summary.creditsSettled)}`}
                                hint={t('supplierpage.returns_hint', '{{n}} returns', { n: summary.returns })}
                                tone="text-emerald-700"
                            />
                            <Stat
                                label={t('supplierpage.closing', 'Closing balance')}
                                value={rupees(summary.closingBalance)}
                                hint={t('supplierpage.closing_hint', 'Before any payment made')}
                                tone="text-[#2D8A8A]"
                                strong
                            />
                        </div>
                    )}

                    {/* Sent and not yet credited. Reported beside the balance and
                        deliberately never inside it — see the module header. */}
                    {summary && toPaise(summary.claimsOutstanding) > 0 && (
                        <div data-testid="ledger-claims-open" className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
                            <Undo2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {t('supplierpage.claims_note', '{{value}} has been claimed from this distributor and not yet credited. It is NOT in the balance: netting an unclaimed credit off the payable short-pays them on the strength of a credit note that may never arrive.', {
                                value: rupees(summary.claimsOutstanding),
                            })}
                        </div>
                    )}

                    {ledger?.balanceExcludesPayments && (
                        <div className="flex items-start gap-2 rounded-xl border border-[#4ABDBD]/40 bg-[#4ABDBD]/5 px-3 py-2 text-[11px] text-[#2D8A8A]">
                            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            {t('supplierpage.balance_caveat_short', 'Payments already made are not deducted anywhere on this account — this is what was bought, not what is left to pay.')}
                        </div>
                    )}

                    {/* ---------------------------------- movements ---- */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-700 mb-2">
                            {t('supplierpage.movements', 'Receipts and returns')}
                        </h3>

                        {loading && rows.length === 0 ? (
                            <div className="flex items-center gap-2 text-xs text-slate-400 py-4">
                                <Loader2 className="w-4 h-4 animate-spin" /> {t('supplierpage.loading', 'Loading...')}
                            </div>
                        ) : (
                            <div className="overflow-x-auto rounded-md border border-gray-200">
                                <table data-testid="supplier-ledger-table" className="w-full border-collapse text-[11px]">
                                    <thead>
                                        <tr className="bg-gray-50 text-gray-600">
                                            <th className="p-2 text-left font-bold uppercase tracking-wider">{t('supplierpage.date', 'Date')}</th>
                                            <th className="p-2 text-left font-bold uppercase tracking-wider">{t('supplierpage.document', 'Document')}</th>
                                            <th className="p-2 text-left font-bold uppercase tracking-wider">{t('supplierpage.reference', 'Reference')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('supplierpage.credit', 'Purchased')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('supplierpage.debit', 'Credited')}</th>
                                            <th className="p-2 text-right font-bold uppercase tracking-wider">{t('supplierpage.running_balance', 'Balance')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* THE BROUGHT-FORWARD ROW, ON PAGE ONE ONLY.
                                            The running balance column is only
                                            readable if the number it starts from is
                                            on the page; without it the first row's
                                            balance appears not to follow from
                                            anything. On page two it would be a lie —
                                            the rows above it already carried the
                                            account forward. */}
                                        {page === 1 && ledger && (
                                            <tr data-testid="ledger-brought-forward" className="border-b border-gray-100 bg-slate-50/60">
                                                <td className="p-2 text-slate-400">—</td>
                                                <td className="p-2 font-semibold text-slate-500" colSpan={2}>
                                                    {from
                                                        ? t('supplierpage.bf_row_period', 'Brought forward (before {{from}})', { from: dateOnly(from) })
                                                        : t('supplierpage.bf_row', 'Opening balance')}
                                                </td>
                                                <td className="p-2" />
                                                <td className="p-2" />
                                                <td className="p-2 text-right font-bold text-gray-700">{rupees(ledger.broughtForward)}</td>
                                            </tr>
                                        )}

                                        {rows.length > 0 ? rows.map((r) => {
                                            const isReturn = r.docType === 'return';
                                            // A return that has been SENT but not yet
                                            // credited moves the balance by nothing:
                                            // credit and debit are both zero and the
                                            // running total is unchanged. Rendered
                                            // with the claim called out, because two
                                            // blank money columns on a row otherwise
                                            // read as a bug rather than as the fact
                                            // that the supplier has not paid up.
                                            const claimP = toPaise(r.claimOutstanding);
                                            return (
                                                <tr
                                                    key={r.docId}
                                                    data-testid={`ledger-row-${r.docId}`}
                                                    className="border-b border-gray-100 hover:bg-slate-50/60"
                                                >
                                                    <td className="p-2 whitespace-nowrap text-gray-600">
                                                        {istDay(r.date)}
                                                        {/* A settled return is dated at
                                                            the CREDIT NOTE, not at the
                                                            despatch, because that is
                                                            when the balance moved. The
                                                            despatch date travels beside
                                                            it so the row can still be
                                                            found by the day the goods
                                                            went back. */}
                                                        {r.settledAt && r.documentDate && istDay(r.documentDate) !== istDay(r.date) && (
                                                            <div className="text-[10px] text-slate-400">
                                                                {t('supplierpage.sent_on', 'sent {{date}}', { date: istDay(r.documentDate) })}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="flex items-center gap-1.5">
                                                            {isReturn
                                                                ? <Undo2 className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                                                                : <PackageCheck className="w-3.5 h-3.5 text-[#2D8A8A] flex-shrink-0" />}
                                                            <span className="font-mono font-semibold text-gray-700">{r.docNo}</span>
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 capitalize">
                                                            {isReturn
                                                                ? t('supplierpage.doc_return', 'Purchase return · {{status}}', { status: r.status })
                                                                : t('supplierpage.doc_grn', 'Goods receipt')}
                                                        </div>
                                                    </td>
                                                    <td className="p-2 text-gray-600">
                                                        <div>{r.reference || '—'}</div>
                                                        {r.referenceDate && (
                                                            <div className="text-[10px] text-slate-400">{dateOnly(r.referenceDate)}</div>
                                                        )}
                                                    </td>
                                                    <td className="p-2 text-right text-gray-700">
                                                        {toPaise(r.credit) ? rupees(r.credit) : '—'}
                                                    </td>
                                                    <td className="p-2 text-right">
                                                        {toPaise(r.debit) ? (
                                                            <span className="text-emerald-700 font-semibold">{rupees(r.debit)}</span>
                                                        ) : claimP > 0 ? (
                                                            <span
                                                                data-testid={`ledger-claim-${r.docId}`}
                                                                title={t('supplierpage.claim_hint', 'Claimed from the supplier and not yet credited — the balance has not moved')}
                                                                className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold whitespace-nowrap"
                                                            >
                                                                {t('supplierpage.claimed', '{{value}} claimed', { value: rupees(r.claimOutstanding) })}
                                                            </span>
                                                        ) : '—'}
                                                    </td>
                                                    <td className="p-2 text-right font-bold text-gray-700">{rupees(r.balance)}</td>
                                                </tr>
                                            );
                                        }) : (
                                            <tr>
                                                <td colSpan={6} className="p-6 text-center text-gray-500">
                                                    {from || to
                                                        ? t('supplierpage.no_movements_period', 'Nothing was received from or returned to this distributor in that period.')
                                                        : t('supplierpage.no_movements', 'Nothing has been received from this distributor yet. A goods receipt is what puts a purchase on their account.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-3">
                                <button
                                    data-testid="ledger-prev"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1 || loading}
                                    className={`px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold ${page <= 1 || loading ? 'opacity-40 cursor-not-allowed text-gray-400' : 'text-gray-600 hover:bg-gray-50 cursor-pointer'}`}
                                >
                                    {t('supplierpage.older', 'Earlier')}
                                </button>
                                <span className="text-[11px] text-slate-400">
                                    {t('supplierpage.page_of', 'Page {{page}} of {{total}}', { page, total: totalPages })}
                                </span>
                                <button
                                    data-testid="ledger-next"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages || loading}
                                    className={`px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold ${page >= totalPages || loading ? 'opacity-40 cursor-not-allowed text-gray-400' : 'text-gray-600 hover:bg-gray-50 cursor-pointer'}`}
                                >
                                    {t('supplierpage.newer', 'Later')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </motion.aside>
        </motion.div>
    );
}

const Stat = ({ label, value, hint, tone = 'text-gray-700', strong = false }) => (
    <div className={`rounded-xl border px-3 py-2 ${strong ? 'border-[#4ABDBD]/50 bg-[#4ABDBD]/5' : 'border-gray-200 bg-white'}`}>
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className={`text-sm font-bold ${tone}`}>{value}</div>
        {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
);
